import React, { useEffect, useRef } from 'react'
import ReactDOM from 'react-dom'
import PopoverContent from 'components/ui/popover2/PopoverContent'
type Props = {
  activator: React.ReactNode
  content: React.ReactNode
  open: boolean
  // Optional preferred position
  position?: 'bottom' | 'left' | 'right' | 'top'
  onClose: () => void
}

// TODO: Rename existing Popover to PopoverMenu
/**
 * @deprecated Use `Popover` from `components/ds` instead. Gradual migration recommended.
 * @see {@link components/ds/Popover}
 * @migration
 * - Replace with DS Popover compound component:
 *   ```tsx
 *   // Legacy
 *   <Popover
 *     activator={<Button>Click me</Button>}
 *     content={<div>Popover content</div>}
 *     open={isOpen}
 *     position="bottom"
 *     onClose={handleClose}
 *   />
 *
 *   // DS alternative
 *   <Popover.Root open={isOpen} onOpenChange={setIsOpen}>
 *     <Popover.Trigger>
 *       <Button>Click me</Button>
 *     </Popover.Trigger>
 *     <Popover.Content side="bottom">
 *       <div>Popover content</div>
 *     </Popover.Content>
 *   </Popover.Root>
 *   ```
 * - Prop mapping:
 *   - activator → Wrap with `<Popover.Trigger>`
 *   - content → Place inside `<Popover.Content>`
 *   - open → `open` prop on Popover.Root
 *   - position → `side` prop on Popover.Content ("top" | "right" | "bottom" | "left")
 *   - onClose → Use `onOpenChange` on Popover.Root
 * - DS Popover handles portal rendering and positioning automatically via Radix
 * - Custom positioning logic may need adjustment when migrating
 *
 * A popover component that displays content in a portal positioned relative to an activator element
 * @param activator - React node that triggers the popover (button, link, etc.)
 * @param content - Content to display inside the popover
 * @param open - Whether the popover is currently open/visible
 * @param position - Preferred position for the popover relative to activator (default: auto-calculated)
 * @param onClose - Callback function called when the popover should close
 * @returns Activator element with conditionally rendered popover content in a portal
 */
const Popover = ({
  activator,
  content,
  open,
  position,
  onClose,
}: Props): React.ReactNode | React.ReactNode => {
  const containerRef = useRef<HTMLElement | null>(null)
  const targetRef = useRef<HTMLDivElement | null>(null)
  useEffect(() => {
    containerRef.current =
      document.getElementById('app') || document.getElementById('__next')
  }, [])

  const renderPortal = (container: HTMLElement) => {
    return ReactDOM.createPortal(
      <PopoverContent
        position={position}
        targetRef={targetRef}
        onClose={onClose}
      >
        {content}
      </PopoverContent>,
      container,
    )
  }

  if (!content) {
    return activator
  }

  return (
    <>
      <div ref={targetRef}>{activator}</div>
      {open && containerRef.current && renderPortal(containerRef.current)}
    </>
  )
}

export default Popover
